import React, {
	createContext,
	useEffect,
	useState,
	useMemo,
	useCallback,
	useRef,
} from 'react';
import { Helmet } from 'react-helmet';
import { ConfigProvider, message } from 'antd';
import PropTypes from 'prop-types';

import {
	loginWithCredentials,
	logoutFirebase,
	onAuthStateHasChanged,
	refreshCurrentUserSession,
} from '../firebase/auth';

import Logo from '../assets/icons/cadira-icon.webp';

import { coreAppData } from '../constant/core';
import { SINGLE_ACTIVE_LOGIN_ENABLED } from '../constant/features';
import { useNotification } from '../hooks/useNotification';
import {
	beginPendingAuthSessionTakeover,
	clearStoredAuthSession,
	finalizeStoredAuthSession,
	getStoredAuthSessionId,
	getStoredAuthSessionUserId,
	isPendingAuthSessionTakeover,
} from '../helper/authSessionStorage';
import {
	consumeAuthSessionNotice,
	queueAuthSessionNotice,
} from '../helper/authSessionNotice';
import {
	claimActiveAuthSession,
	subscribeToActiveAuthSession,
} from '../service/authSessionService';

const defaultSessionData = {
	userId: null,
	name: null,
	profilePicture: null,
	latihanType: null,
	gender: null,
	membershipType: null,
	birthDate: null,
	phoneVerified: false,
	phoneVerificationRequired: false,
	verificationLevel: 'basic',
	menuConfig: null,
	testTypes: null,
	membershipStatus: null,
	scoringSummary: null,
};

const initialState = {
	...defaultSessionData,
	status: 'checking',
};

export const AppContext = createContext({});

export const AppProvider = ({ children }) => {
	const [session, setSession] = useState(initialState);
	const [messageApi, contextHolder] = message.useMessage();
	const forcedLogoutRef = useRef(false);
	useEffect(() => {
		onAuthStateHasChanged(setSession);
	}, []);

	const handleLogOut = useCallback(async () => {
		await logoutFirebase(); // ← await agar Firebase benar-benar logout dulu
		clearStoredAuthSession();
		setSession({
			...defaultSessionData,
			status: 'no-authenticated',
		});
	}, []);

	const validateAuth = useCallback(
		(userData) => {
			// Handle registration-in-progress statuses
			if (
				userData.status === 'REGISTRATION_EMAIL' ||
				userData.status === 'REGISTRATION_PHONE'
			) {
				setSession({
					...defaultSessionData,
					status: userData.status,
					userId: userData.userId,
				});
				return;
			}

			if (userData.userId) {
				setSession({
					userId: userData.userId,
					name: userData.name,
					profilePicture: userData.profilePicture,
					latihanType: userData.latihanType,
					gender: userData.gender,
					membershipType: userData.membershipType,
					birthDate: userData.birthDate,
					phoneVerified: userData.phoneVerified === true,
					phoneVerificationRequired:
						userData.phoneVerificationRequired === true,
					verificationLevel: userData.verificationLevel || 'basic',
					menuConfig: userData.menuConfig ?? null,
					testTypes: userData.testTypes ?? null,
					membershipStatus: userData.membershipStatus ?? null,
					scoringSummary: userData.scoringSummary ?? null,
					status: 'authenticated',
				});
				return;
			}

			handleLogOut();
		},
		[handleLogOut]
	);

	const checking = useCallback(
		() => setSession((prev) => ({ ...prev, status: 'checking' })),
		[]
	);

	const handleLoginWithCredentials = useCallback(
		async (password, email) => {
			checking();
			const userData = await loginWithCredentials({ email, password });
			validateAuth(userData);
			return userData;
		},
		[checking, validateAuth]
	);

	const refreshSession = useCallback(async () => {
		const sessionData = await refreshCurrentUserSession();
		if (sessionData) setSession(sessionData);
	}, []);

	const triggerMessage = useCallback(
		(type, text) => {
			messageApi.open({
				type,
				content: text,
			});
		},
		[messageApi]
	);

	// FCM push notification setup — pass userId agar re-register saat ganti akun
	const {
		needsPermission: needsNotifPermission,
		requestPermission: requestNotifPermission,
	} = useNotification(
		session.status === 'authenticated' ? session.userId : null
	);

	useEffect(() => {
		if (session.status !== 'no-authenticated') return;

		const pendingNotice = consumeAuthSessionNotice();
		if (!pendingNotice) return;

		triggerMessage(pendingNotice.type, pendingNotice.text);
	}, [session.status, triggerMessage]);

	useEffect(() => {
		if (!SINGLE_ACTIVE_LOGIN_ENABLED) {
			clearStoredAuthSession();
			return undefined;
		}

		if (session.status !== 'authenticated' || !session.userId) {
			forcedLogoutRef.current = false;
			return undefined;
		}

		let disposed = false;
		let unsubscribe = () => {};
		let claimInFlight = false;

		const ensureOwnership = async () => {
			if (claimInFlight || disposed) return;

			claimInFlight = true;
			try {
				let sessionId = getStoredAuthSessionId();
				const storedUserId = getStoredAuthSessionUserId();
				let pendingTakeover = isPendingAuthSessionTakeover();

				if (storedUserId && storedUserId !== session.userId) {
					clearStoredAuthSession();
					sessionId = null;
					pendingTakeover = false;
				}

				if (!sessionId) {
					if (disposed) return;

					sessionId = beginPendingAuthSessionTakeover();
					pendingTakeover = true;
				}

				if (!pendingTakeover) return;

				await claimActiveAuthSession(sessionId, {
					platform: 'web',
					userAgent:
						typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
				});

				if (!disposed) {
					finalizeStoredAuthSession({
						userId: session.userId,
						sessionId,
					});
				}
			} catch (error) {
				// eslint-disable-line no-empty
			} finally {
				claimInFlight = false;
			}
		};

		const forceLogoutFromRemoteLogin = async () => {
			if (forcedLogoutRef.current || disposed) return;
			forcedLogoutRef.current = true;
			queueAuthSessionNotice({
				type: 'warning',
				text: 'Akun ini login di browser atau perangkat lain. Sesi kamu telah berakhir.',
			});
			await handleLogOut();
		};

		ensureOwnership();

		unsubscribe = subscribeToActiveAuthSession(
			session.userId,
			(snapshot) => {
				if (disposed || forcedLogoutRef.current) return;

				const localSessionId = getStoredAuthSessionId();
				const pendingTakeover = isPendingAuthSessionTakeover();
				const remoteSessionId = snapshot.exists()
					? snapshot.data()?.activeSessionId || null
					: null;
				const isCachedSnapshot = snapshot.metadata.fromCache;

				if (!localSessionId) {
					ensureOwnership();
					return;
				}

				if (!remoteSessionId) {
					ensureOwnership();
					return;
				}

				if (remoteSessionId === localSessionId) {
					if (pendingTakeover) {
						finalizeStoredAuthSession({
							userId: session.userId,
							sessionId: localSessionId,
						});
					}
					return;
				}

				if (pendingTakeover) {
					ensureOwnership();
					return;
				}

				// Jangan force logout dari snapshot cache karena nilainya bisa stale,
				// terutama sesudah logout manual lalu login ulang cepat.
				if (isCachedSnapshot) {
					return;
				}

				forceLogoutFromRemoteLogin();
			},
			() => {
				if (!disposed) {
					ensureOwnership();
				}
			}
		);

		return () => {
			disposed = true;
			unsubscribe();
		};
	}, [session.status, session.userId, handleLogOut, triggerMessage]);

	const { primary, secondary, burgerMenuBg, contentBgColor } =
		coreAppData.colorPalletes;

	const themeProvider = {
		token: {
			colorPrimary: primary,
			colorSecondary: secondary,
			fontFamily: 'Poppins',
		},
		components: {
			// 	Input: {
			// 		lineHeight: 2.2,
			// 	},
			Button: {
				fontWeight: 500,
				defaultBorderColor: primary,
				defaultColor: primary,
			},
			Layout: {
				siderBg: burgerMenuBg,
				colorBgHeader: burgerMenuBg,
				colorBgLayout: contentBgColor,
				headerPadding: 0,
			},
			Menu: {
				itemColor: primary,
				itemSelectedBg: primary,
				itemHoverColor: primary,
				itemSelectedColor: burgerMenuBg,
				fontSize: 14,
				iconSize: 18,
				itemBorderRadius: 16,
				itemPaddingInline: 22,
			},
			// 	Table: {
			// 		headerBg: color4,
			// 		cellPaddingBlockMD: 22,
			// 	},
			// 	Radio: {
			// 		buttonSolidCheckedBg: colorSmall2,
			// 	},
			// 	Descriptions: {
			// 		labelBg: color5,
			// 	},
			// 	Tree: {
			// 		directoryNodeSelectedBg: 'green',
			// 	},
			// 	Tabs: {
			// 		itemSelectedColor: colorSmall2,
			// 		inkBarColor: colorSmall2,
			// 		itemHoverColor: colorSmall1,
			// 		itemColor: 'rgb(148, 148, 148)',
			// 	},
			// 	Collapse: {
			// 		headerBg: color5,
			// 	},
			// 	DatePicker: {
			// 		activeBorderColor: colorSmall2,
			// 		hoverBorderColor: colorSmall2,
			// 	},
		},
	};

	const providerValue = useMemo(() => {
		return {
			session,
			handleLoginWithCredentials,
			handleLogOut,
			triggerMessage,
			refreshSession,
			needsNotifPermission,
			requestNotifPermission,
		};
	}, [
		session,
		handleLoginWithCredentials,
		handleLogOut,
		triggerMessage,
		refreshSession,
		needsNotifPermission,
		requestNotifPermission,
	]);

	return (
		<AppContext.Provider value={providerValue}>
			<Helmet>
				<title>{coreAppData.shortName}</title>
				<link rel="icon" type="png" href={Logo} />
				<link rel="apple-touch-icon" type="png" href={Logo} />
				<meta name="description" content={`${coreAppData.shortName}`} />
				<meta
					name="description"
					key="description"
					content={`${coreAppData.shortName}`}
				/>

				<meta
					property="og:title"
					key="og:title"
					content={`${coreAppData.shortName}`}
				/>
				<meta property="og:locale" key="og:locale" content="id-ID" />
				<meta charSet="utf-8" />
				<meta property="og:type" key="og:type" content="website" />
				<meta
					property="og:description"
					key="og:description"
					content={`${coreAppData.shortName}`}
				/>
				<meta property="og:image" key="og:image" content={Logo} />
			</Helmet>
			{contextHolder}
			<ConfigProvider theme={themeProvider}>{children}</ConfigProvider>
		</AppContext.Provider>
	);
};

AppProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
